import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { LoadAssetContainerAsync } from '@babylonjs/core/Loading/sceneLoader';
import '@babylonjs/loaders/glTF';
import { createWorld } from './core/engine';
import { loadClips } from './core/animLoader';
import { Input } from './player/input';
import { Animator } from './player/animator';
import { PlayerController } from './player/controller';
import { ThirdPersonCamera } from './player/camera';
import { buildWeaponProps, WEAPONS } from './player/weapons';
import { setupCharacterLook } from './player/appearance';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3, Vector3 } from '@babylonjs/core/Maths/math';
import { DefaultRenderingPipeline } from '@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/defaultRenderingPipeline';
import { ColorCurves } from '@babylonjs/core/Materials/colorCurves';
import { ImageProcessingConfiguration } from '@babylonjs/core/Materials/imageProcessingConfiguration';
import { Dummies } from './world/dummies';
import { Effects, makeSoftShadowTexture } from './world/effects';
import { Terrain } from './world/terrain';
import { SkySystem } from './world/sky';
import { Vegetation } from './world/vegetation';
import { Roads } from './world/roads';
import { Buildings } from './world/buildings';
import { Doors } from './world/doors';
import { Props } from './world/props';
import { PerfHud } from './ui/perf';
import { MapOverlay } from './ui/mapoverlay';
import { Ambience } from './core/ambience';
import { DebugMenu } from './ui/debugmenu';
import { Hud } from './ui/hud';
import { GameAudio } from './core/audio';
import { Sfx } from './core/sfx';
import { buildAnimDebug } from './ui/animdebug';
import { AnimLog } from './ui/animlog';
import { Forage } from './world/forage';
import { Animals } from './world/animals';
import { Cooking } from './world/cooking';
import { Loot } from './world/loot';
import { NetClient } from './net/client';
import { RemotePlayers } from './net/remotes';
import { JoinScreen } from './ui/join';
import { Inventory } from './game/inventory';
import { ITEMS } from './game/items';
import { Stats } from './game/stats';
import { NoiseSystem } from './game/noise';
import { Infected, INF_ZONE_Y, ST as INF_ST } from './world/zombies';
import { InventoryUI } from './ui/inventory';
import { createQuery, gatherCells } from './world/query.mjs';
import { meleeSwing } from './world/melee-arc.mjs';

async function boot() {
  const canvas = document.getElementById('game');
  // The MAP10K world is the game now. ?gym=1 keeps the flat movement/combat
  // arena for animation/feel work (smoke.mjs uses it); ?map=1 still accepted.
  const q = new URLSearchParams(location.search);
  const mapMode = q.get('gym') !== '1';
  const { engine, scene, shadows } = createWorld(canvas, { map: mapMode });
  const hud = new Hud();
  const input = new Input(canvas);
  input.on('help', () => hud.toggleHelp());

  hud.loading('Loading character…');
  const c = await LoadAssetContainerAsync('assets/models/character.glb', scene);
  c.addAllToScene();
  c.animationGroups.forEach(g => g.stop()); // ignore the rig's embedded clip

  // root (we move) -> pivot -> glb hierarchy
  const root = new TransformNode('playerRoot', scene);
  const pivot = new TransformNode('playerPivot', scene);
  pivot.parent = root;
  const glbRoot = c.meshes.find(m => !m.parent) || c.meshes[0];
  glbRoot.parent = pivot;

  // receiveShadows is set by setupCharacterLook: the survivor receives ONLY his
  // own character-scoped key's map (appearance.js), never the world sun's
  // 240-840 m box — see the note there.
  for (const m of c.meshes) { shadows.addShadowCaster(m); m.isPickable = false; }
  setupCharacterLook(scene, c.meshes); // clothing/material readability pass (src/player/appearance.js)

  // map of every mixamorig transform node for retargeting + overlays
  const nodes = [...c.transformNodes, ...c.meshes];
  const boneMap = new Map();
  for (const n of nodes) if (n.name.startsWith('mixamorig:')) boneMap.set(n.name, n);

  hud.loading('Loading animations…');
  const clips = await loadClips(scene, nodes, (done, total) => {
    if (done === total) hud.loading('Ready — click to play');
    else if (done % 20 === 0) hud.loading(`Loading animations… ${done}/${total}`);
  });

  const animator = new Animator(clips);
  const camera = new ThirdPersonCamera(scene, input);
  const effects = new Effects(scene);
  const dummies = new Dummies(scene, engine, camera, shadows);
  const props = buildWeaponProps(scene, boneMap, shadows);
  const audio = new GameAudio();
  const sfx = await new Sfx(audio).init(); // CC0 sample library (index only; decode on demand)
  audio.sfx = sfx;
  canvas.addEventListener('click', () => {
    audio.ensure(); // gesture unlocks WebAudio
    sfx.warm('step.grass', 'step.dirt', 'step.twigs', 'gun.rifle', 'gun.pistol', 'melee.whoosh',
      'animal.chicken', 'animal.goat', 'animal.sheep');
  });
  const world = { scene, dummies, effects, props, audio, sfx };
  // dummies are built before `world` exists; their melee resolver needs it
  dummies.world = world;
  let terrain = null, sky = null, blob = null, veg = null, roads = null, buildings = null, props2 = null, perf = null, overlay = null, ambience = null, forage = null, animals = null, cooking = null, loot = null, doors = null;
  const params = new URLSearchParams(location.search);
  if (mapMode) {
    hud.loading('Loading terrain…');
    terrain = await new Terrain(scene).init();
    world.getGroundY = (x, z) => terrain.getHeight(x, z);
    // water-type mask (0 land, 1 sea, 2 river, 3 lake) — drinking needs to
    // tell fresh water from salt
    try {
      const wbuf = new Uint8Array(await (await fetch('assets/map10k/water.bin')).arrayBuffer());
      const WRES = 1025;
      const wc = (terrain.half * 2) / (WRES - 1);
      world.waterAt = (x, z) => {
        const ix = Math.max(0, Math.min(WRES - 1, Math.round((x + terrain.half) / wc)));
        const iz = Math.max(0, Math.min(WRES - 1, Math.round((terrain.half - z) / wc)));
        return wbuf[iz * WRES + ix];
      };
    } catch { world.waterAt = () => 0; }
    // roads load before vegetation so the analytic road mask can veto scatter
    // (both the init-time overgrowth pass and the runtime grass ring)
    hud.loading('Loading roads…');
    roads = await new Roads(scene, terrain).init();
    hud.loading('Loading vegetation…');
    const vegPre = new Vegetation(scene, terrain);
    // exact road mask beats the coarse offline roadmask.bin — no grass mid-lane
    if (roads.roadMask) {
      const coarseMask = vegPre.maskAt.bind(vegPre);
      vegPre.maskAt = (x, z) => (roads.roadMask(x, z) ? 1 : coarseMask(x, z));
    }
    veg = await vegPre.init();
    world.pushOut = (pos) => veg.pushOut(pos);
    hud.loading('Loading buildings…');
    buildings = await new Buildings(scene, terrain).init();
    world.pushOut = (pos) => { veg.pushOut(pos); buildings.pushOut(pos); };
    // interior floors override terrain once the player steps through a door
    world.getGroundY = (x, z) => {
      const fy = buildings.floorAt(x, z);
      const ty = terrain.getHeight(x, z);
      return fy != null && fy > ty ? fy : ty;
    };
    hud.loading('Loading props…');
    // roads passed in so prop bedding decals can tell tarmac from soil
    props2 = await new Props(scene, terrain, roads).init();
    forage = new Forage(scene, terrain, veg);
    animals = new Animals(scene, terrain, veg, sfx);
    cooking = new Cooking(scene, terrain, buildings, sfx);
    loot = new Loot(scene, terrain, buildings);
    doors = new Doors(scene, buildings.doors, terrain, sfx);
    world.animals = animals;
    animals.world = world;   // same shared resolver as dummies/infected
    world.doors = doors;
    hud.loading('Loading sky…');
    sky = await new SkySystem(scene, [terrain, veg, roads, buildings, props2]).init(params.get('mood') || 'overcast');
    perf = new PerfHud(engine, scene, { terrain, veg });
    overlay = await new MapOverlay(terrain, roads).init();

    // M11 world hooks: footstep surface, noise model stub (Phase 4), loot sites (Phase 2)
    world.pushOut = (pos) => { veg.pushOut(pos); buildings.pushOut(pos); props2.pushOut(pos); doors.pushOut(pos); };
    // vault-probe: point-i-OBB med eksplicit y-bånd (pushOut er en 1,7 m
    // kapsel og kan ikke se vindueshuller/tynde hegn på probeafstand)
    world.blocked = (x, z, y0, y1) =>
      buildings.blockedBand(x, z, y0, y1) || props2.blockedBand(x, z, y0, y1)
      || doors.blockedBand(x, z, y0, y1) || veg.trunkBlocked(x, z);
    world.buildings = buildings; // test-hook (floorAt-assertions)
    veg.floorFn = (x, z) => buildings.floorAt(x, z); // intet græs gennem gulvbrædder
    const SURF_BY_BIOME = ['sand', 'sand', 'grass', 'dirt', 'grass', 'grass', 'grass',
      'dirt', 'asphalt', 'asphalt', 'asphalt'];
    world.surfaceAt = (x, z) => {
      if (buildings.floorAt(x, z) != null) return 'wood'; // interior floorboards
      const m = veg.maskAt(x, z);
      if (m === 1) return 'asphalt';
      if (m === 2) return 'dirt';
      const b = veg.biomeAt(x, z);
      if ((b === 8 || b === 9) && terrain.getHeight(x, z) > 640) return 'snow'; // above the snowline
      if (b >= 4 && b <= 6) return 'forest'; // grass steps + twig-crunch layer
      return SURF_BY_BIOME[b] || 'grass';
    };
    world.noiseFactor = (x, z) => {
      const b = veg.biomeAt(x, z);
      return b >= 4 && b <= 6 ? 0.8 : b === 10 ? 1.2 : 1; // forest muffles, town echoes
    };
    world.lootSites = buildings.lootSites;

    // ---- THE WORLD QUERY (src/world/query.mjs) -------------------------
    // One analytic collider query over the hashes the player ALREADY collides
    // with, so "what does this ray hit" and "is A->B blocked" finally have an
    // answer. Mesh picking was rejected on purpose: every world mesh here is
    // isPickable = false, and making 10x10 km of terrain, walls, doors and
    // trees pickable would hang an octree cost on every frame for an answer
    // that is wanted a few times a second.
    //
    // TWO instances behind ONE handle, and this is not an accident:
    //   ray()            MUST see bodies. That is what makes a shot at a
    //                    remote player, an infected or an animal resolve at
    //                    all — the old mesh-name pick never did.
    //   segmentBlocked() MUST NOT. Melee occlusion and AI line-of-sight run
    //                    the segment TO a target, so its far end sits INSIDE
    //                    that target's own cylinder: a body-aware segment test
    //                    would report every swing and every sight line as
    //                    blocked by the very thing it is aimed at.
    // Consumers keep using the one documented name, world.query, and get the
    // right instance for the question they are asking. `.full` / `.cover` are
    // there for anyone who needs the other one on purpose.
    //
    // maxExtent is load-bearing. buildings.js registers a whole sealed
    // building under its CENTRE cell only, so the lookup radius has to cover
    // the largest centre-to-corner reach in the hash or a bullet goes through
    // the facade of the big blocks — the exact defect this service exists to
    // kill. It is MEASURED, not written down: buildings.addObb tracks
    // maxObbExtent as chunks stream in, and this is a closure over it rather
    // than a snapshot, because the hash is empty at boot and a bigger
    // archetype in a future towns.json must not silently understate it. (It
    // was a hard-coded 55, hand-measured off today's map at 53.7 m.)
    //
    // propsNear carries a `shrink`, for the opposite reason: props.js addCol()
    // stores (hw + 0.3, hd + 0.3) because pushOut() has to keep a BODY RADIUS
    // clear of a prop. Fed in raw, a 0.12 m fence slab is a 0.80 m wall, a
    // 0.30 m lamp post stops a round passing 0.44 m beside it, every impact
    // decal floats 0.3 m off its surface, and — worst — props.pushOut parks
    // the player ON that skin, so the muzzle is INSIDE it and the round is
    // eaten at distance zero. 0.3 is exact and uniform: addCol adds it to
    // every prop unconditionally, so subtracting it here recovers the true
    // extents without touching the movement contract that wanted the pad.
    // buildings.hash gets no shrink on purpose — its pads are NOT uniform
    // (0.35 on a sealed footprint, 0.15 on a wall segment's length, 0 on
    // interior partitions and stairs), so one number would eat real geometry.
    const qProviders = (scratch) => ({
      heightAt: (x, z) => terrain.getHeight(x, z),
      groundSurfaceAt: (x, z) => world.surfaceAt(x, z),
      maxHeight: terrain.H.master.hMax,
      minHeight: terrain.H.master.hMin,
      cellSize: terrain.H.master.cellSize,
      worldHalf: terrain.half,
      // near(x, z, r) must return every collider whose CENTRE is within r —
      // gatherCells is the shared cell scan, and `scratch` is reused because
      // the query consumes what a provider returns before calling it again.
      obbsNear: { near: (x, z, r) => gatherCells(buildings.hash, 16, x, z, r, scratch.b), maxExtent: () => buildings.maxObbExtent + 1 },
      propsNear: { near: (x, z, r) => gatherCells(props2.colHash, 16, x, z, r, scratch.p), maxExtent: 8, shrink: 0.3 },
      doorsNear: { near: (x, z, r) => gatherCells(doors.hash, 16, x, z, r, scratch.d), maxExtent: 2 },
      trunksNear: { near: (x, z, r) => gatherCells(veg.trunkHash, 8, x, z, r, scratch.t), maxExtent: 2 },
    });
    const qCover = createQuery(qProviders({ b: [], p: [], d: [], t: [] }));
    const qFull = createQuery({
      ...qProviders({ b: [], p: [], d: [], t: [] }),
      bodiesNear: (x, z, r) => collectBodies(x, z, r),
    });
    world.query = {
      ray: (o, d, max) => qFull.ray(o, d, max),
      // `opts` must be forwarded: melee-arc.mjs passes { openEnds: true } so a
      // collider the attacker or the target is STANDING IN does not count as
      // the thing hiding them from each other. Swallowing it here is how a
      // player with their back to a wall became melee-immune.
      segmentBlocked: (ax, az, bx, bz, y0, y1, opts) => qCover.segmentBlocked(ax, az, bx, bz, y0, y1, opts),
      stats: qFull.stats,
      coverStats: qCover.stats,
      full: qFull,
      cover: qCover,
    };

    // ---- BODIES: the source that makes a ray resolve a shot at something
    // ALIVE. Nothing owned this before, which is why "gun PvP against remote
    // players never resolves" and "animals cannot be shot" were separate bug
    // reports: the old hitscan picked on mesh NAMES ('ground' / 'crate' /
    // 'dummy*') and a remote player, a wolf and a chicken are none of those.
    //
    // Records are the shape query.mjs documents: a vertical cylinder plus the
    // two zone cuts. For the infected the cuts are INF_ZONE_Y, the SAME
    // numbers zombies.js sizes its pickable capsules from, so a query ray and
    // a legacy mesh pick classify one shot identically.
    //
    // `entity` is the record's OWN damage tag, in exactly the shape
    // controller.js's targetTag()/_damageTarget() already reads off hitbox
    // metadata — { netPlayer } for a player, { animal } for an animal,
    // { infected, owner } for an infected (controller.js:28-34, 349-376). That
    // is the contract that file was written to, so wiring bodies in needs no
    // change there at all. The tags are cached per entity, not rebuilt per
    // lookup: collectBodies runs once per corridor chunk per ray.
    // Records are POOLED, not allocated. collectBodies runs once per corridor
    // chunk per ray and a shot fires two rays of up to seven chunks each, over
    // every remote plus up to INF.stream.capGlobal (150) streamed infected plus
    // every animal — so a fresh object literal per body per lookup was
    // thousands of short-lived objects per second of sustained fire, landing in
    // the nursery during the exact frames a firefight cannot afford a GC hitch.
    // The query reads what a provider returns before it calls that provider
    // again (its own documented contract), and `entity` is a cached tag rather
    // than the record, so one buffer of reusable records is safe.
    const bodyScratch = [];
    let bodyUsed = 0;
    const bodyRec = () => {
      let r = bodyScratch[bodyUsed];
      if (!r) bodyScratch[bodyUsed] = r = { x: 0, z: 0, r: 0, y0: 0, y1: 0, headY0: 0, legsY1: 0, entity: null, surface: 'flesh' };
      bodyUsed++;
      return r;
    };
    const bodyTags = new WeakMap();        // entity -> its damage tag
    const ANIMAL_BODY = {                  // cylinder per species, metres
      chicken: [0.18, 0.45], goat: [0.35, 0.85], sheep: [0.38, 0.9], wolf: [0.35, 0.9],
    };
    const bodyTag = (entity, make) => {
      let t = bodyTags.get(entity);
      if (!t) { t = make(); bodyTags.set(entity, t); }
      return t;
    };
    const bodyOut = [];
    function put(rec) { bodyOut.push(rec); return rec; }
    function collectBodies(x, z, r) {
      bodyOut.length = 0;
      bodyUsed = 0;
      const r2 = r * r;
      if (remotes) {
        for (const [rid, rp] of remotes.remotes) {
          if (rp.dead || !rp.root.isEnabled()) continue;
          const p = rp.root.position;
          if ((p.x - x) * (p.x - x) + (p.z - z) * (p.z - z) > r2) continue;
          // STANCE. The cylinder used to be 1.8 m tall whatever the remote
          // was doing, so a prone player could be hit by shooting the air a
          // metre over their back and crouching bought nothing. There is no
          // stance field on the wire, but the base CLIP name is there (and is
          // what the server's own gait table reads), and pickLoco() names
          // every crouch clip `crouch_*` and every prone clip `prone_*`.
          // Only the top of the cylinder moves: the radius stays 0.4 so no
          // shot that lands today starts missing, and the posed mesh capsules
          // (remotes.js) remain the accurate surface for a body lying down —
          // _pickTarget still reaches them, because the query no longer
          // reports a nearer phantom hit in the air above.
          const b = rp.base || '';
          const top = b.startsWith('prone') ? 0.7 : b.startsWith('crouch') ? 1.35 : 1.8;
          const o = put(bodyRec());
          o.x = p.x; o.z = p.z; o.r = 0.4; o.y0 = p.y; o.y1 = p.y + top;
          o.headY0 = p.y + top - 0.35; o.legsY1 = p.y + top * 0.5;
          o.entity = bodyTag(rp, () => ({ netPlayer: rid }));
        }
      }
      if (zombies) {
        for (const v of zombies.views.values()) {
          if (v.state === INF_ST.dead) continue;
          if ((v.x - x) * (v.x - x) + (v.z - z) * (v.z - z) > r2) continue;
          // the { infected, owner } proxy is the same shape zombies.js parks
          // on its capsules, so both answers land in Infected.gunHit
          const o = put(bodyRec());
          o.x = v.x; o.z = v.z;
          // a crawler lies down: one low cylinder, and no leg zone left to
          // cripple (it is already crippled), so legsY1 sits at its feet
          if (v.crawler) {
            o.r = 0.45; o.y0 = v.y; o.y1 = v.y + 0.75;
            o.headY0 = v.y + 0.45; o.legsY1 = v.y;
          } else {
            o.r = 0.4; o.y0 = v.y; o.y1 = v.y + INF_ZONE_Y.crown;
            o.headY0 = v.y + INF_ZONE_Y.headBot; o.legsY1 = v.y + INF_ZONE_Y.legsTop;
          }
          o.entity = bodyTag(v, () => ({ infected: v.id, owner: zombies }));
        }
      }
      if (animals) {
        for (const a of animals.animals) {
          if (a.state === 'dead') continue;
          if ((a.pos.x - x) * (a.pos.x - x) + (a.pos.z - z) * (a.pos.z - z) > r2) continue;
          const [ar, ah] = ANIMAL_BODY[a.species] || [0.35, 0.9];
          // Animals have no zone multipliers — Animals.damage(a, dmg) takes no
          // zone — so the cuts are pushed outside the cylinder and every hit
          // reports 'body' rather than inventing a chicken headshot.
          const o = put(bodyRec());
          o.x = a.pos.x; o.z = a.pos.z; o.r = ar; o.y0 = a.pos.y; o.y1 = a.pos.y + ah;
          o.headY0 = a.pos.y + ah + 1; o.legsY1 = a.pos.y - 1;
          o.entity = bodyTag(a, () => ({ animal: a }));
        }
      }
      return bodyOut;
    }

    // camera collision (polish): slab-test the anchor→camera segment against
    // the same OBB truth the player collides with (walls carry y-ranges so the
    // camera may still fly OVER buildings), then keep it above the ground
    const obbSegT = (a, dx, dy, dz, e) => {
      const [bx, bz, hw, hd, cos, sin, y0 = -1e9, y1 = 1e9] = e;
      const M = 0.22; // camera radius
      const lax = (a.x - bx) * cos + (a.z - bz) * sin;
      const laz = -(a.x - bx) * sin + (a.z - bz) * cos;
      const ldx = dx * cos + dz * sin;
      const ldz = -dx * sin + dz * cos;
      let t0 = 0, t1 = 1;
      const slabs = [[lax, ldx, -hw - M, hw + M], [a.y, dy, y0, y1 + M], [laz, ldz, -hd - M, hd + M]];
      for (const [o, dd, lo, hi] of slabs) {
        if (Math.abs(dd) < 1e-8) {
          if (o < lo || o > hi) return 1;
        } else {
          let ta = (lo - o) / dd, tb = (hi - o) / dd;
          if (ta > tb) { const tmp = ta; ta = tb; tb = tmp; }
          if (ta > t0) t0 = ta;
          if (tb < t1) t1 = tb;
          if (t0 > t1) return 1;
        }
      }
      if (t1 < 0 || t0 > 1) return 1;
      return t0 <= 0 ? 0.05 : Math.max(0, t0 - 0.02);
    };
    world.cameraClip = (a, b) => {
      const dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z;
      let tMin = 1;
      const cells = new Set();
      const steps = Math.max(1, Math.ceil(Math.hypot(dx, dz) / 8));
      for (let i = 0; i <= steps; i++) {
        const cx = Math.floor((a.x + (dx * i) / steps) / 16);
        const cz2 = Math.floor((a.z + (dz * i) / steps) / 16);
        for (let ox = -1; ox <= 1; ox++) for (let oz = -1; oz <= 1; oz++) cells.add(`${cx + ox}_${cz2 + oz}`);
      }
      for (const key of cells) {
        for (const src of [buildings.hash, props2.colHash]) {
          const list = src.get(key);
          if (!list) continue;
          for (const e of list) {
            const t = obbSegT(a, dx, dy, dz, e);
            if (t < tMin) tMin = t;
          }
        }
      }
      return tMin;
    };
    camera.clipFn = world.cameraClip;
    camera.groundFn = world.getGroundY;
    world.ceilingAt = (x, z) => buildings.ceilingAt(x, z);
    camera.ceilFn = world.ceilingAt; // indoors: never rise through the ceiling

    // procedural ambience (M12): wind/birds/waves/creaks from biome + mood
    const BIOME_KEYS = ['sea', 'beach', 'meadow', 'farmland', 'forest_birch', 'forest_pine',
      'forest_mixed', 'swamp', 'rock', 'alpine', 'urban'];
    ambience = await new Ambience(audio, (x, z) => BIOME_KEYS[veg.biomeAt(x, z)] || 'meadow', sfx).init();
    sky.targets.push(ambience);

    // post FX chain (M4): fxaa, bloom, grain, vignette, desaturated grade + SSAO
    if (params.get('fx') !== '0') {
      const pipe = new DefaultRenderingPipeline('fx', true, scene, [camera.cam]);
      pipe.fxaaEnabled = true;
      pipe.bloomEnabled = true;
      pipe.bloomThreshold = 0.85;
      pipe.bloomWeight = 0.1; // bloom belongs on the sun disc and water spec only
      pipe.bloomKernel = 48;
      pipe.grainEnabled = true;
      pipe.grain.intensity = 3; // 9 sparkled like white noise on night blacks
      pipe.grain.animated = true;
      pipe.imageProcessingEnabled = true;
      const ip = pipe.imageProcessing;
      ip.toneMappingEnabled = true; // HDR skies need rolloff, not clipping
      ip.toneMappingType = ImageProcessingConfiguration.TONEMAPPING_ACES;
      ip.contrast = 1.16;
      ip.vignetteEnabled = true;
      ip.vignetteWeight = 0.8; // AAA restraint — 1.7 read as a phone filter
      const curves = new ColorCurves();
      curves.globalSaturation = -15; // the DayZ "wet Sunday" grade
      ip.colorCurvesEnabled = true;
      ip.colorCurves = curves;
      // NOTE: SSAO2 is deliberately absent — it black-crushes Standard/PBR
      // meshes in this mixed ShaderMaterial scene (found the hard way in M9)
    }

    // cheap grounding shadow until terrain receives real shadow maps (M10) —
    // soft radial falloff so it reads as ambient occlusion, not a hard disc
    blob = MeshBuilder.CreateDisc('blobShadow', { radius: 0.62, tessellation: 24 }, scene);
    blob.rotation.x = Math.PI / 2;
    const bmat = new StandardMaterial('blobMat', scene);
    bmat.diffuseColor = bmat.emissiveColor = Color3.Black();
    bmat.disableLighting = true;
    bmat.opacityTexture = makeSoftShadowTexture(scene);
    bmat.alpha = 0.55;
    blob.material = bmat;
    blob.isPickable = false;
  }
  const player = new PlayerController(root, pivot, boneMap, animator, input, hud, camera, world);
  // The melee resolver (src/world/melee-arc.mjs) reads `player.attack` — the
  // object controller.js builds per swing and clears when the move ends — as
  // the swing id, so a four-window combo cannot apply full damage four times
  // to one target. Nothing else in the game touches world.player.
  world.player = player;
  if (terrain) {
    // DayZ-style spawn: one of the coastal spawn hamlets from the settlement
    // plan (random), or ?spawn=x,z for testing
    let sx = 3500, sz = -1000;
    const sp = params.get('spawn');
    if (sp) {
      [sx, sz] = sp.split(',').map(Number);
    } else {
      const spawns = (overlay?.stl?.entries || []).filter((e) => e.spawnPoint
        && terrain.getHeight(e.pos[0], e.pos[1]) > 0.5); // the coast wobble drowned one hamlet
      if (spawns.length) {
        const pick = spawns[Math.floor(Math.random() * spawns.length)];
        sx = pick.pos[0];
        sz = pick.pos[1];
      }
    }
    root.position.set(sx, terrain.getHeight(sx, sz), sz);
  }
  animator.setBase('idle');
  const animdbg = buildAnimDebug(animator, input);
  input.on('animdbg', () => animdbg.toggle());
  // rolling animation log (F4) — lists every clip the character plays
  const animLog = new AnimLog();
  animator.onAnim = (e) => animLog.push(e);
  input.on('animlog', () => animLog.toggle());

  // ---- survival: inventory / stats / foraging (M13 Phase 2) ----
  const inventory = new Inventory();
  inventory.equip('tshirt');
  inventory.equip('shorts');
  inventory.equip('backpack_courier');
  inventory.addItem('water_bottle');
  inventory.addItem('matches');
  const stats = new Stats(player, hud, audio);
  // ---- noise: the sense the infected feed on (docs/INFECTED.md Z20–Z28) ----
  // One emitter for the whole session. `world.noise` is the hook every other
  // system reports into (controller.js reaches it through `this.world`); the
  // systems below hold it as `.noise` because they have no world reference.
  const noise = new NoiseSystem(world);
  world.noise = noise;
  world.noiseState = () => noise.playerSenses({
    x: root.position.x, z: root.position.z, stance: player.stance,
    speed: Math.hypot(player.velocity?.x || 0, player.velocity?.z || 0),
    sneak: input.sneak, mood: sky?.mood?.key,
  });
  stats.noise = noise;
  if (loot) loot.noise = noise;
  if (doors) doors.noise = noise;       // hinges + latch (Z118)
  if (cooking) cooking.noise = noise;   // a lit fire is a steady beacon
  // ...and a strike costs a match. Without this `cooking.inventory` stays null,
  // light() takes its no-inventory fallback and fires are free and infinite —
  // the whole match economy was unreachable (cooking.js:86-93).
  if (cooking) cooking.inventory = inventory;
  effects.noise = noise;                // breaking glass
  noise.sink = (ev) => net?.event(ev); // relayed as {k:'noise'} once online (Z27)
  const invUI = new InventoryUI(inventory, stats, {
    eat: (it, def) => {
      stats.eat(def);
      inventory.removeItem(it, 1);
      hud.flashMove(`${def.kind === 'drink' ? 'Drank' : def.kind === 'med' ? 'Used' : 'Ate'} ${def.name}`);
    },
    drop: (it) => {
      // READ THE QUANTITY FIRST. removeItem() decrements the record in place
      // (inv-core.mjs:199), so by the next line `it.qty` is 0 — dropItem then
      // normalised that 0 up to 1 (forage.js:230) and a 4× stack of crackers
      // became one cracker on the ground with three destroyed, while the toast
      // read "Dropped Crackers" with no ×N because it reads the same zero.
      const q = it.qty;
      const name = ITEMS[it.id].name;
      inventory.removeItem(it, q);
      forage?.dropItem(it.id, q, root.position);
      hud.flashMove(`Dropped ${q > 1 ? q + '× ' : ''}${name}`);
    },
    equip: (it) => {
      // equip() returns the WHOLE spill list and empties the old container
      // unconditionally (inv-core.mjs:268), so every stack is either carried or
      // in this array — dropping only the first one deleted the tail from the
      // game. dropAll() exists for exactly this; it normalises qty and refuses
      // unknown ids. Measured before the fix: a full expedition pack swapped
      // for a courier bag spilled 22 stacks and destroyed 21 of them.
      const spilled = inventory.equip(it);
      if (spilled?.length) {
        const n = forage?.dropAll(spilled, root.position) || 0;
        // ...and say so. The old path was silent, so the loss had no symptom.
        if (n) hud.flashMove(`No room — dropped ${n} item${n > 1 ? 's' : ''} at your feet`, { sev: 'warn' });
      }
    },
    patch: (it, def) => {
      if (!stats.patch()) { hud.flashMove('You are not bleeding'); return; }
      inventory.removeItem(it, 1);
      if (def.eat) stats.eat(def);
      sfx.play('loot.bandage', { vol: 0.5, slice: 1.2 });
      hud.flashMove(`Patched your wounds with ${def.name.toLowerCase()}`);
    },
    canCook: () => !!cooking?.nearFire(root.position),
    cook: (it, def) => {
      const fire = cooking.nearFire(root.position);
      inventory.removeItem(it, 1);
      inventory.addItem(def.cookedId, 1);
      hud.flashMove(fire?.stove
        ? `Roasted ${ITEMS[def.cookedId].name} on the stove`
        : `Roasted ${ITEMS[def.cookedId].name} on a stick over the fire`);
    },
    lightFire: () => {
      if (!cooking) return;
      hud.flashMove(cooking.light(root.position, player.heading).msg);
    },
    // inventory screen: what is in the player's hands (DayZ hands slot)
    hands: () => {
      const w = player.weapon;
      if (!w || w === 'fists') return null;
      return { weapon: w, label: WEAPONS[w]?.label || w };
    },
    // inventory screen: ground/vicinity panel — loot and dropped items lying
    // around the player, each takeable with a click
    vicinity: () => {
      const p = root.position;
      const out = [];
      const R = 9;
      for (const it of loot?.items || []) {
        if (Math.hypot(it.x - p.x, it.z - p.z) > R) continue;
        out.push({ id: it.id, qty: it.qty || 1, d: Math.hypot(it.x - p.x, it.z - p.z),
          take: () => hud.flashMove(loot.pick(it, inventory).msg) });
      }
      for (const g of forage?.ground || []) {
        if (Math.hypot(g.x - p.x, g.z - p.z) > R) continue;
        out.push({ id: g.id, qty: g.qty || 1, d: Math.hypot(g.x - p.x, g.z - p.z),
          take: () => hud.flashMove(forage.pick({ type: 'ground', g }, inventory).msg) });
      }
      for (const s of forage?.spots || []) {
        if (Math.hypot(s.x - p.x, s.z - p.z) > R) continue;
        out.push({ id: s.id, qty: s.qty || 1, d: Math.hypot(s.x - p.x, s.z - p.z),
          take: () => hud.flashMove(forage.pick({ type: 'forage', s }, inventory).msg) });
      }
      return out.sort((a, b) => a.d - b.d);
    },
  }, canvas);
  input.on('inventory', () => invUI.toggle());
  input.on('quickslot', ({ slot }) => invUI.useQuickslot(slot));
  // shared G-target: nearest of forage spot / dropped item / animal corpse
  const interactTarget = () => {
    if (!forage || player.dead) return null;
    const p = root.position;
    const cands = [];
    const t = forage.nearest(p);
    if (t) {
      const s = t.type === 'forage' ? t.s : t.g;
      cands.push({ d: Math.hypot(s.x - p.x, s.z - p.z), label: forage.label(t), act: () => forage.pick(t, inventory) });
    }
    const c = animals?.nearestCorpse(p);
    if (c) {
      cands.push({ d: Math.hypot(c.pos.x - p.x, c.pos.z - p.z), label: `Butcher ${c.sp.label.toLowerCase()}`, act: () => animals.butcher(c, inventory) });
    }
    const l = loot?.nearest(p);
    if (l) {
      cands.push({ d: Math.hypot(l.x - p.x, l.z - p.z), label: loot.label(l), act: () => loot.pick(l, inventory) });
    }
    // an infected you put down still has pockets (docs/INFECTED.md Z132)
    const ic = loot?.nearestCorpse?.(p);
    if (ic) {
      cands.push({ d: Math.hypot(ic.x - p.x, ic.z - p.z), label: loot.corpseLabel(ic), act: () => loot.searchCorpse(ic, inventory) });
    }
    // Pass the look direction: door-pick.mjs gates its facing cone on
    // (fx || fz), so with no forward vector FACE_DOT/FACE_ANY never run and a
    // doorway behind you still wins the prompt over the crate in front of you.
    const dr = doors?.nearest(p, { x: Math.sin(camera.yaw), z: Math.cos(camera.yaw) });
    if (dr) {
      cands.push({ d: Math.hypot(dr.cx - p.x, dr.cz - p.z), label: doors.label(dr), act: () => doors.toggle(dr) });
    }
    // drinking from rivers/lakes — risky without purification tablets. The
    // water mask is ~10 m cells, so "at the water's edge" is close enough.
    if (world.waterAt) {
      let fresh = false;
      for (const [ox, oz] of [[0, 0], [6, 0], [-6, 0], [0, 6], [0, -6]]) {
        if (world.waterAt(p.x + ox, p.z + oz) >= 2) { fresh = true; break; }
      }
      if (fresh) {
        const tab = inventory.containers().flatMap((c2) => c2.items).find((i) => i.id === 'purification_tablets');
        cands.push({
          d: 2.0, // real pickups within arm's reach win over the shoreline
          label: tab ? 'Drink purified water (uses 1 tablet)' : 'Drink lake water (risky)',
          act: () => {
            sfx.play('loot.drink', { vol: 0.5, slice: 1.2 });
            if (tab) {
              inventory.removeItem(tab, 1);
              stats.eat({ eat: { water: 30 } });
              return { ok: true, msg: 'Drank purified water' };
            }
            const bad = Math.random() < 0.45; // giardia roulette
            stats.eat({ eat: { water: 30, ...(bad ? { poison: { dmg: 16, delay: 45 } } : {}) } });
            return { ok: true, msg: 'Drank straight from the lake' };
          },
        });
      }
    }
    cands.sort((a, b) => a.d - b.d);
    return cands[0] || null;
  };
  input.on('interact', (e) => {
    const t = interactTarget();
    if (t) { hud.flashMove(t.act().msg); return; }
    // F with nothing to grab still swings the gun as a melee (old F behaviour)
    if (e?.fromF) input.emit('gunMelee');
  });
  // K toggles death in the controller (its listener runs first); if the player
  // just respawned, reset the survival stats too
  input.on('die', () => { if (!player.dead) stats.respawn(); });
  let vitalsT = 0;

  // wolves bite the LOCAL player (each client simulates its own wildlife)
  if (animals) {
    animals.onBite = (a) => {
      if (player.dead) return;
      const [lo, hi] = a.sp.biteDmg;
      // `dir` is the bearing the blow TRAVELS (attacker → victim), the same
      // convention infected-sim.mjs sends and hurt.mjs expects — without it
      // screenAngle() returns null and the directional arc can never fire.
      stats.damage(lo + Math.random() * (hi - lo), {
        kind: 'wolf', heading: player.heading,
        dir: Math.atan2(root.position.x - a.pos.x, root.position.z - a.pos.z),
      });
      // wound() is silent at the 5-wound cap and returns false to say so, so
      // `if (bleed) wound()` meant the worst moment in a mauling produced NO
      // toast at all. Fall back to the bite line whenever it had no news.
      if (!(Math.random() < a.sp.bleedChance && stats.wound())) hud.flashMove('A wolf bit you!');
      sfx.play('melee.punch', { vol: 0.5, slice: 0.5 });
      camera.addRecoil?.(0.6);
    };
  }

  // ---- multiplayer (map world only — the gym stays a solo test arena;
  // ?solo=1 keeps a map session off the shared server for tests/debugging) ----
  let net = null, remotes = null, joinUI = null;
  if (mapMode && params.get('solo') !== '1') {
    remotes = new RemotePlayers(scene, clips, shadows);
    const toast = (msg) => hud.flashMove(msg);
    net = new NetClient({
      onWelcome: (m) => {
        // shared-world truth replaces this session's empty sets
        m.world.forage.forEach((k) => forage.picked.add(k));
        m.world.loot.forEach((k) => loot.taken.add(k));
        m.world.ground.forEach((g) => forage.applyDrop(g));
        m.world.fires.forEach((f) => cooking.applyFire(f));
        m.world.animalsDead.forEach((id) => animals.applyDead(id));
        // wolves are simulated on the server while online — everyone sees the
        // same pack in the same place
        animals.netWolves = true;
        animals.applyServerWolves(m.world.wolves || []);
        // …and so is the horde, when the server is new enough to own it: the
        // key is present (possibly empty) exactly when server/zombies.mjs is
        // running, and the first call drops this client's local sim for good
        if (m.world.infected) zombies?.applyServerInfected(m.world.infected);
        m.world.butchered.forEach((id) => animals.applyButcher(id));
        forage.lastBuild.set(1e9, 0, 1e9);
        loot.lastBuild.set(1e9, 0, 1e9);
        // your saved character: inventory, vitals and where you logged out
        if (m.saved?.inv) inventory.restore(m.saved.inv);
        if (m.saved?.stats) {
          stats.health = Math.max(5, Math.min(100, m.saved.stats.health ?? 100));
          stats.hunger = Math.max(0, Math.min(100, m.saved.stats.hunger ?? 100));
          stats.thirst = Math.max(0, Math.min(100, m.saved.stats.thirst ?? 100));
        }
        if (Array.isArray(m.saved?.pos) && m.saved.pos.every(Number.isFinite)) {
          const [sx, sy, sz] = m.saved.pos;
          root.position.set(sx, Math.max(sy, world.getGroundY(sx, sz)), sz);
        }
        for (const p of m.players) {
          remotes.add(p.id, p.name).then(() => { if (p.s) remotes.applyState(p.id, p.s); });
        }
        joinUI?.refresh();
        hud.setOnline(net.online.size);
        if (m.players.length) toast(`${m.players.length} other survivor${m.players.length > 1 ? 's' : ''} in the world`);
      },
      onJoin: ({ id, name }) => {
        remotes.add(id, name);
        joinUI?.refresh();
        hud.setOnline(net.online.size);
        toast(`${name} joined the world`);
      },
      onLeave: ({ id, name }) => {
        remotes.remove(id);
        joinUI?.refresh();
        hud.setOnline(net.online.size);
        toast(`${name} left`);
      },
      onRename: ({ id, name }) => remotes.setName(id, name),
      onWolves: (w) => animals.applyServerWolves(w),
      onInfected: (rows) => zombies?.applyServerInfected(rows || []),
      onStates: (states) => {
        for (const [id, s] of Object.entries(states)) {
          if (remotes.remotes.has(id)) remotes.applyState(id, s);
          else remotes.add(id, net.online.get(id) || 'Survivor');
        }
      },
      onEvent: (ev, from) => {
        switch (ev.k) {
          case 'forage': forage.applyPick(ev.key); break;
          case 'loot': loot.applyTake(ev.key); break;
          case 'drop': forage.applyDrop(ev); break;
          case 'ground-take': forage.applyGroundTake(ev.uid); break;
          case 'fire': cooking.applyFire(ev); break;
          case 'animal': animals.applyDead(ev.id); break;
          case 'butcher': animals.applyButcher(ev.id); break;
          // an infected corpse someone else emptied (Z137). Under server truth
          // the horde's ids are shared, so this id names the SAME body on every
          // screen — without consuming the relay two players each loot it.
          case 'corpse-search': loot.applyCorpseSearch(ev.id); break;
          // someone else's gunshot / door / campfire (Z27). Under server truth
          // the server's own sim already heard it and applyPing is a no-op;
          // against the old server this is what makes a neighbour's rifle pull
          // the horde on THIS screen.
          case 'noise': zombies?.applyPing(ev); break;
          case 'swing': remotes.applySwing(from, ev); break;
          case 'shot': {
            // someone else's muzzle flash + tracer + distant report
            const o = new Vector3(...ev.o), e = new Vector3(...ev.e);
            effects.muzzle(o);
            effects.tracer(o, e);
            const d = Vector3.Distance(o, root.position);
            if (d < 400) sfx.play('gun.rifle', { vol: Math.min(0.5, 12 / Math.max(8, d)), delay: d / 340, slice: 1.1 });
            break;
          }
          case 'hit': {
            if (ev.target !== net.id || player.dead) break;
            // an infected swung at you on the server (Z50–Z52): the same
            // handler the local sim uses, so blocking still cuts it to 30 %
            if (ev.kind === 'infected') { zombies?.onHit?.({ ...ev, target: 'local' }); break; }
            stats.damage(ev.dmg || 10);
            if (ev.kind === 'wolf') {
              // the server rolled the bleed with the bite; wound() returns
              // false at the 5-wound cap, and a bite with no toast at all is
              // the wrong answer there
              if (!(ev.bleed && stats.wound())) hud.flashMove('A wolf bit you!');
              sfx.play('melee.punch', { vol: 0.5, slice: 0.5 });
            } else {
              if (Math.random() < (ev.kind === 'gun' ? 0.35 : 0.25)) stats.wound();
              hud.flashMove(`${net.online.get(from) || 'Someone'} hit you!`);
              // NO player.hurt here: stats.damage() above owns the pain grunt
              // now (stats._hurtFeedback). Keeping this one played two takes on top of
              // each other for every PvP hit — three with a bleed roll.
            }
            camera.addRecoil?.(0.5);
            break;
          }
        }
      },
      onStatus: (state) => {
        joinUI?.setStatus(state);
        hud.setOnline(state === 'online' ? net.online.size : null);
      },
    });
    // outgoing: world events + 10 Hz pose
    forage.onNet = loot.onNet = cooking.onNet = animals.onNet = (ev) => net.event(ev);
    forage.uidPrefix = net.id.slice(0, 8) + '-';
    // PvP: shots/swings resolve on the attacker's screen, the victim's client
    // applies the damage (family server — honesty assumed)
    world.onPlayerHit = (id, dmg, kind) => net.event({ k: 'hit', target: id, dmg, kind });
    world.onShot = (o, e) => net.event({
      k: 'shot',
      o: [+o.x.toFixed(1), +o.y.toFixed(1), +o.z.toFixed(1)],
      e: [+e.x.toFixed(1), +e.y.toFixed(1), +e.z.toFixed(1)],
    });
    // PvP melee. This was the fourth copy of the arc test (reach + 0.4,
    // dot 0.5, no y band, no occlusion) — the copy that mattered most, because
    // it is the one players notice: you could knife a man through the wall he
    // was hiding behind, and from the floor below him. Same shared resolver as
    // dummies/animals/infected now, same net event, same damage.
    world.remoteHit = (pos, heading, reach, dmg) => {
      let hits = 0;
      const swing = meleeSwing(world, pos, heading, reach);
      for (const [rid, r] of remotes.remotes) {
        if (r.dead || !r.root.isEnabled()) continue;
        const p = r.root.position;
        if (!swing.test(p.x, p.y, p.z, r)) continue;
        net.event({ k: 'hit', target: rid, dmg, kind: 'melee' });
        hits++;
      }
      return hits;
    };
    // replicate one-shot attack/reload clips so swings are visible to others
    for (const fnName of ['playUpper', 'playFull']) {
      const orig = animator[fnName].bind(animator);
      animator[fnName] = (name, rate, onDone, fade) => {
        net.event({ k: 'swing', fn: fnName, name, rate });
        return orig(name, rate, onDone, fade);
      };
    }
    net.stateFn = () => ({
      p: [+root.position.x.toFixed(2), +root.position.y.toFixed(2), +root.position.z.toFixed(2)],
      y: +player.heading.toFixed(3),
      b: animator.base?.name, r: +(animator.base?.rate ?? 1).toFixed(2),
      h: animator.hold?.name || null,
      w: player.weapon,
      d: player.dead,
      // the two sense scalars the server's infected hear you with (Z22):
      // n = noise radius in metres, v = visibility multiplier
      ...(world.noiseState?.() || {}),
    });
    // persistence: every 20 s and on the way out the door
    const snapshot = () => ({
      inv: inventory.toJSON(),
      stats: { health: stats.health, hunger: stats.hunger, thirst: stats.thirst },
      pos: [+root.position.x.toFixed(1), +root.position.y.toFixed(1), +root.position.z.toFixed(1)],
    });
    setInterval(() => { if (net.connected) net.save(snapshot()); }, 20000);
    window.addEventListener('beforeunload', () => { if (net.connected) net.save(snapshot()); });
    joinUI = new JoinScreen(net, () => {
      audio.ensure(); // the PLAY click is a real user gesture
      canvas.requestPointerLock?.();
    });
    joinUI.setStatus(net.connected ? 'online' : 'connecting');
  }

  // ---- the infected (docs/INFECTED.md) ----------------------------------
  // src/world/zombies.js owns the rigs, the voices and the hit surface; the
  // brain is src/world/infected-sim.mjs. Online it is the SERVER that runs
  // that brain (server/zombies.mjs) and this client only mirrors the snapshot
  // rows arriving on `onInfected`; `?solo=1` keeps simulating locally.
  let zombies = null;
  if (mapMode && params.get('noinf') !== '1') { // ?noinf=1 = an empty town, for benches
    zombies = new Infected(scene, {
      terrain, buildings, veg, sfx, effects, camera, engine, shadows,
      boneMap, player, input, world, remotes, loot,
      groundY: (x, z) => world.getGroundY(x, z),
      mood: () => sky?.mood?.key,
    });
    world.zombies = zombies;
    // melee: chain onto the same hook remote players use, so controller.js's
    // existing dummies/animals/remoteHit call list already reaches them
    const prevRemoteHit = world.remoteHit;
    world.remoteHit = (pos, heading, reach, dmg) =>
      (prevRemoteHit?.(pos, heading, reach, dmg) ?? 0) + zombies.meleeHit(pos, heading, reach, dmg);
    // an infected landed a swing (Z50–Z52): blocking inside a 60° frontal cone
    // cuts it to 30 % and cancels bleed/infection
    zombies.onHit = (ev) => {
      if (player.dead || stats.dead || ev.target !== 'local') return;
      let d = Math.abs(((player.heading - (ev.dir + Math.PI)) % (Math.PI * 2) + Math.PI * 3) % (Math.PI * 2) - Math.PI);
      const blocked = !!input.block && d < Math.PI / 3;
      // This is the ONE hit path in the game that already knows where the blow
      // came from (infected-sim.mjs:745) and it was throwing the bearing away
      // after the block test — which is why #hurt-arc and its 20 lines of CSS
      // could never become visible. Same convention the block cone uses above.
      stats.damage(ev.dmg * (blocked ? 0.3 : 1),
        { kind: 'infected', dir: ev.dir, heading: player.heading });
      if (!blocked) {
        if (!(ev.bleed && stats.wound())) hud.flashMove('The infected claws at you!');
        if (ev.infect) stats.infect?.();
      } else hud.flashMove('Blocked!');
      sfx.play('creature.zombie.attack', { vol: 0.55, slice: 1.1 });
      camera.addRecoil?.(blocked ? 0.3 : 0.7);
    };
    // under server truth the client resolves nothing: damage is reported as
    // {k:'inf-dmg'} and the verdict comes back on the next snapshot (Z57)
    if (net) zombies.onNet = (ev) => net.event(ev);
    zombies.init();
    window.__zombies = zombies; // smoke-test hook
  }

  window.__game = { scene, engine, camera, player, animator, props, boneMap, audio, terrain, sky, world,
    inventory, stats, forage, invUI, animals, cooking, loot, doors, hud, net, remotes, zombies }; // tooling hook
  window.__ambience = ambience; // sound-harness hook
  new DebugMenu({ scene, camera, player, props, terrain }); // F10

  scene.onAfterAnimationsObservable.add(() => {
    player.applyOverlays(engine.getDeltaTime() / 1000);
  });

  engine.runRenderLoop(() => {
    const dt = Math.min(engine.getDeltaTime() / 1000, 0.05);
    // anim debug panel can freeze the player brain while clips keep playing
    if (window.__freezePlayer) animator.update(dt);
    else player.update(dt, camera.yaw);
    dummies.update(dt, terrain ? root.position : null);
    effects.update(dt);
    if (terrain) {
      terrain.update(root.position, camera.cam);
      veg?.update(root.position, camera.cam);
      roads?.update(camera.cam);
      buildings?.update(camera.cam);
      props2?.update(camera.cam);
      sky?.update(dt, camera.cam.globalPosition.y);
      perf?.update(dt);
      overlay?.update(dt, root.position, player.heading);
      ambience?.update(dt, root.position);
      if (blob) {
        blob.position.set(root.position.x, terrain.getHeight(root.position.x, root.position.z) + 0.06, root.position.z);
        // The blob is a pre-shadow-map contact cheat: a sun-agnostic disc pinned
        // under the boots. Where the real cast-shadow system is running it
        // contradicts the key (a soft ellipse pointing nowhere beside a
        // body-length sun shadow), so it cross-fades OUT with shadow strength
        // and only survives indoors / in the soft-shadow moods.
        const st = scene.__ashShadows?.p.w ?? 0;
        blob.material.alpha = 0.55 * Math.max(0, 1 - st);
        blob.setEnabled(blob.material.alpha > 0.02);
      }
      forage.update(root.position);
      animals.update(dt, root.position, input.sprint && !player.dead, camera.yaw, player.dead);
      cooking.update(dt);
      loot.update(root.position);
      doors.update(root.position, dt);
      remotes?.update(dt);
      const near = invUI.open ? null : interactTarget();
      invUI.setPrompt(near ? near.label : null);
    }
    stats.update(dt, input.sprint && !player.dead);
    vitalsT += dt;
    // the vitals column + the infection plate that sits beside it (Z125–Z131)
    if (vitalsT > 0.25) {
      vitalsT = 0;
      invUI.updateVitals();
      hud.setInfection(stats);
      // The one call hud.js was waiting for. Until it existed the HUD polled
      // window.__game.world.noiseState() on a setInterval that never stopped,
      // in the gym world too (where noiseState is undefined and the poll did
      // nothing at all); the first real setNoise() clears that timer for good.
      hud.setNoise(world.noiseState?.());
    }
    camera.update(dt, root.position, {
      stance: player.stance, lean: player.lean, focus: player.focus,
      aiming: player.aiming,
      freelook: player.freelookYaw != null, moveYaw: player.freelookYaw ?? camera.yaw,
      mode: input.sprint && !player.dead ? 'sprint' : 'normal',
    });
    scene.render();
  });
  window.addEventListener('resize', () => engine.resize());
}

boot().catch(e => {
  console.error(e);
  document.body.innerHTML = `<pre style="color:#f66;padding:2em">${e.stack || e}</pre>`;
});
